import { handleStudentLogout } from '@/common/utils/auth';
import { DailiesManager } from '@/game/GameComponents/PhaserGame/Managers/DailiesManager';
import { DuelManager } from '@/game/GameComponents/PhaserGame/Managers/DuelManager';
import EventsManager from '@/game/GameComponents/PhaserGame/Managers/EventsManager';
import { GameManager } from '@/game/GameComponents/PhaserGame/Managers/GameManager';
import { InventoryManager } from '@/game/GameComponents/PhaserGame/Managers/InventoryManager';
import SceneManager from '@/game/GameComponents/PhaserGame/Managers/SceneManager';
import { StudentManager } from '@/game/GameComponents/PhaserGame/Managers/StudentManager';
import { PhaserAudioManager } from '@/game/GameComponents/PhaserGame/Utils/PhaserAudioManager';
import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { EventBus } from './EventBus';
import StartGame from './main';
import GAME_EVENTS from './Utils/gameEventsDef';

export interface IRefPhaserGame {
  game?: Phaser.Game;
  scene?: Phaser.Scene;
}

interface IProps {
  currentActiveScene?: (scene_instance: Phaser.Scene) => void;
}

const PhaserGame = forwardRef<IRefPhaserGame, IProps>(
  ({ currentActiveScene }, ref) => {
    const navigate = useNavigate();

    const game = useRef<Phaser.Game>();
    const cleanupTimer = useRef<NodeJS.Timeout>();

    const destroyGame = useCallback(() => {
      // destroy managers
      GameManager.destroyGameManager();
      InventoryManager.destroyInventoryManager();
      DailiesManager.destroyDailiesManager();
      DuelManager.destroyDuelManager();
      StudentManager.destroyStudentManager();
      SceneManager.destroySceneManager();
      EventsManager.destroyEventsManager();
      PhaserAudioManager.destroyPhaserAudioManager();

      // remove event listeners
      EventBus.removeAllListeners();

      // clear audio
      PhaserAudioManager.activeSFX = {};

      // log student out
      handleStudentLogout();

      // destroy phaser game instance
      if (game.current) {
        try {
          console.log('Destroying game');
          game.current.destroy(true);
        } catch (e) {
          console.error('Error destroying game:', e);
        }
        game.current = undefined;
      }
    }, []);

    useLayoutEffect(() => {
      // In strict mode, React runs cleanup then immediately re-mounts.
      // If a deferred cleanup is pending, we are in the re-mount phase — cancel
      // the destruction and register a proper cleanup for the real unmount.
      if (cleanupTimer.current !== undefined) {
        clearTimeout(cleanupTimer.current);
        cleanupTimer.current = undefined;
        return destroyGame;
      }

      // start game
      console.log('Starting new game instance');
      game.current = StartGame('game-container');

      // forward ref
      if (typeof ref === 'function') {
        ref({ game: game.current });
      } else if (ref) {
        ref.current = { game: game.current };
      }

      EventBus.once(GAME_EVENTS.USER_LOGOUT, () => {
        navigate('/');
      });
      EventBus.once(GAME_EVENTS.PLAYER_REQUESTED_ACCOUNT_UPGRADE, () => {
        navigate('/pricing');
      });

      new GameManager();
      new InventoryManager();
      new DailiesManager();
      new DuelManager();
      new StudentManager();
      new EventsManager();

      return () => {
        // Defer destruction so a strict mode re-mount can cancel it before
        // the Phaser game and its plugins are torn down unnecessarily.
        cleanupTimer.current = setTimeout(() => {
          cleanupTimer.current = undefined;
          destroyGame();
        }, 0);
      };
    }, [destroyGame, navigate, ref]);

    useEffect(() => {
      const sceneReadyHandler = (scene: Phaser.Scene) => {
        currentActiveScene?.(scene);
        if (typeof ref === 'function') {
          ref({ game: game.current, scene });
        } else if (ref) {
          ref.current = { game: game.current, scene };
        }
      };
      EventBus.on('current-scene-ready', sceneReadyHandler);
      return () => {
        EventBus.off('current-scene-ready', sceneReadyHandler);
      };
    }, [currentActiveScene, ref]);

    return <div id="game-container" />;
  },
);

export default PhaserGame;
